import { useMemo } from "react";
import type { SafeMeta } from "@/core/logging";
import { useAssessmentStep } from "@/features/assessments/AssessmentStepProvider/AssessmentStepProvider";
import { type AssessmentLogCode, assessmentLogger } from "./AssessmentLogger";

// Pre-binds the three session fields every assessment log must carry
// (`assessmentId`, `subdomainSlug`, `flowType`) so call sites only supply the
// variant-specific context. Works anywhere inside an assessment flow — this
// hook is intentionally NOT scoped to any particular feature (capture,
// pre-scan, contact, etc.). If your component has a repeated discriminator
// (e.g. `stage: "front_capture"`), pass it once via `extraMeta` and it will
// be merged into every call.
//
// Base fields are applied last (`{ ...extraMeta, ...meta, ...base }`) so
// callers can't accidentally overwrite the authoritative session identity,
// but `meta` can still override `extraMeta` when a particular call needs to.
export function useAssessmentLogger(assessmentId: string, extraMeta?: SafeMeta) {
  const {
    subdomainSlug,
    flowType
  } = useAssessmentStep();
  return useMemo(() => {
    const base = {
      assessmentId,
      subdomainSlug,
      flowType
    };
    return {
      info: (code: AssessmentLogCode, meta?: SafeMeta) => assessmentLogger.info(code, {
        ...extraMeta,
        ...meta,
        ...base
      }),
      warn: (code: AssessmentLogCode, meta?: SafeMeta) => assessmentLogger.warn(code, {
        ...extraMeta,
        ...meta,
        ...base
      }),
      error: (code: AssessmentLogCode, meta: SafeMeta | undefined, error: Error) => assessmentLogger.error(code, {
        ...extraMeta,
        ...meta,
        ...base
      }, error)
    };
    // `extraMeta` is a plain object; callers typically pass an inline literal
    // which would change identity every render. We depend on a stable JSON
    // projection so the memo holds across renders when the content is the
    // same (the extra fields are small — stage name, step name, etc.).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId, subdomainSlug, flowType, JSON.stringify(extraMeta ?? null)]);
}